import { useState, type FormEvent } from "react";

const CONTACT_URL = "https://readdy.ai/api/form/daihmnjp14a1h3mr74eg";

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

const topics = ["话题推荐", "内容纠错", "合作洽谈", "其他问题"];

export default function ContactForm() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [messageLength, setMessageLength] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const guard = String(formData.get("company_alt") ?? "").trim();
    if (guard) {
      setStatus("success");
      setFormError("");
      return;
    }

    const name = String(formData.get("name") ?? "").trim();
    const email = String(formData.get("email") ?? "").trim();
    const topic = String(formData.get("topic") ?? "").trim();
    const message = String(formData.get("message") ?? "").trim();

    if (!name) {
      setStatus("error");
      setFormError("请填写你的称呼");
      return;
    }
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus("error");
      setFormError("请输入有效的邮箱地址");
      return;
    }
    if (!topic) {
      setStatus("error");
      setFormError("请选择留言类型");
      return;
    }
    if (message.length < 10) {
      setStatus("error");
      setFormError("留言内容至少 10 个字，方便我们理解你的问题");
      return;
    }
    if (message.length > 500) {
      setStatus("error");
      setFormError("留言内容不能超过 500 个字");
      return;
    }

    setStatus("submitting");
    setFormError("");

    try {
      const payload = new URLSearchParams({ name, email, topic, message }).toString();
      const response = await fetch(CONTACT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload,
      });
      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (!response.ok || parsed?.code !== "OK" || isSpam || !serverMsg) {
        setStatus("error");
        setFormError(
          isSpam || !response.ok
            ? "提交未成功，请稍后重试"
            : String(serverMsg) || "提交未成功，请稍后重试",
        );
        return;
      }

      setStatus("success");
      setFormError("");
      setMessageLength(0);
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后重试");
    }
  };

  return (
    <form
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
    >
      <h2 className="font-heading text-lg font-bold text-foreground-950">在线留言</h2>
      <p className="mt-1 text-xs text-foreground-500">带 * 的为必填项，我们会在工作日内回复。</p>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="flex flex-col gap-1.5">
          <span className="text-sm text-foreground-700">
            称呼 <span className="text-accent-600">*</span>
          </span>
          <input
            type="text"
            name="name"
            required
            maxLength={30}
            placeholder="怎么称呼你"
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-sm text-foreground-700">
            邮箱 <span className="text-accent-600">*</span>
          </span>
          <input
            type="email"
            name="email"
            required
            placeholder="your@email.com"
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </label>
      </div>

      <label className="mt-4 flex flex-col gap-1.5">
        <span className="text-sm text-foreground-700">
          留言类型 <span className="text-accent-600">*</span>
        </span>
        <select
          name="topic"
          required
          defaultValue="话题推荐"
          className="cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
        >
          {topics.map((topic) => (
            <option key={topic} value={topic}>
              {topic}
            </option>
          ))}
        </select>
      </label>

      <label className="mt-4 flex flex-col gap-1.5">
        <span className="flex items-center justify-between text-sm text-foreground-700">
          <span>
            留言内容 <span className="text-accent-600">*</span>
          </span>
          <span className={`text-xs ${messageLength > 500 ? "text-accent-600" : "text-foreground-400"}`}>
            {messageLength}/500
          </span>
        </span>
        <textarea
          name="message"
          required
          rows={5}
          maxLength={500}
          onChange={(event) => setMessageLength(event.target.value.length)}
          placeholder="请描述你想推荐的话题或需要纠错的内容，附上公开来源链接会更快处理"
          className="resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
        ></textarea>
      </label>

      <input
        type="text"
        name="company_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="form-field-guard"
      />

      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="submit"
          disabled={status === "submitting"}
          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
        >
          <i className="ri-send-plane-line"></i>
          {status === "submitting" ? "提交中…" : "提交留言"}
        </button>
        <p className="text-xs text-foreground-400">我们不会将你的信息用于其他用途。</p>
      </div>

      {status === "success" && (
        <p className="mt-4 flex items-center gap-2 rounded-md bg-primary-50 px-3 py-2.5 text-sm text-primary-800">
          <i className="ri-checkbox-circle-line"></i>
          留言已提交，我们会在工作日 24 小时内回复你。
        </p>
      )}
      {status === "error" && (
        <p className="mt-4 flex items-center gap-2 rounded-md bg-accent-50 px-3 py-2.5 text-sm text-accent-800">
          <i className="ri-error-warning-line"></i>
          {formError}
        </p>
      )}
    </form>
  );
}